import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import i18nInst from '@shared/i18n';
import { useCloseOnTabSwitch } from '@shared/hooks/useCloseOnTabSwitch';
import { useBodyScrollLock } from '@shared/hooks/useBodyScrollLock';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary, VerifiedBadge } from '@shared/ui';
import { useHandleVerification } from '@shared/hooks/useHandleVerification';
import { IconCoPublish, IconTag, IconMention, NamedIcon } from '@shared/ui/icons';
import Avatar from '@shared/ui/Avatar';

// Nivo trend chart — lazy so Nivo only downloads when a brand card opens.
const BrandTrendNivo = lazy(() => import('./BrandTrendNivo'));
import PostViewerModal from '@shared/ui/PostViewerModal';
import { SignedImg } from '@shared/ui/SignedMedia';
import { currentLocale } from '@shared/i18n';

/** Detection source → collaboration-type group. */
type CollabGroup = 'copublish' | 'paid' | 'tag' | 'mention' | 'caption' | 'hashtag' | 'other';
const srcGroup = (s: string | null): CollabGroup => {
  switch (s) {
    case 'coauthor': return 'copublish';
    case 'sponsor_tag': return 'paid';
    case 'usertag': return 'tag';
    case 'mention': case 'caption_at': case 'um_apply': return 'mention';
    case 'caption': return 'caption';
    case 'hashtag': return 'hashtag';
    default: return 'other';
  }
};
const GROUP_ORDER: CollabGroup[] = ['copublish', 'paid', 'tag', 'mention', 'caption', 'hashtag', 'other'];
const GROUP_LABEL: Record<CollabGroup, string> = {
  copublish: 'Co-published', paid: 'Paid partnership', tag: 'Tagged', mention: 'Mentioned',
  caption: 'In caption', hashtag: 'Hashtag', other: 'Other',
};
const GROUP_ICON: Partial<Record<CollabGroup, JSX.Element>> = {
  copublish: <IconCoPublish size={15} />, tag: <IconTag size={15} />, mention: <IconMention size={15} />,
};

const engagementOf = (r: { likes: number | null; comments: number | null }) => (r.likes ?? 0) + (r.comments ?? 0);

/** Print stylesheet: during print, show only the (hoisted) mentions overlay, in a light, paginated layout. */
const PRINT_CSS = `
@media print {
  @page { margin: 12mm; }
  body.bm-printing { background: #fff !important; }
  body.bm-printing > * { display: none !important; }
  body.bm-printing > .bm-print-overlay {
    display: block !important; position: static !important; inset: auto !important;
    background: #fff !important; backdrop-filter: none !important;
    padding: 0 !important; margin: 0 !important; overflow: visible !important; z-index: auto !important;
  }
  body.bm-printing .bm-print-root {
    max-width: none !important; width: 100% !important;
    background: #fff !important; color: #111 !important;
    border: 0 !important; box-shadow: none !important; border-radius: 0 !important; padding: 0 !important;
  }
  body.bm-printing .bm-print-root h2 { color: #000 !important; }
  body.bm-printing .bm-no-print { display: none !important; }
  body.bm-printing .bm-grid { grid-template-columns: repeat(3, 1fr) !important; }
  body.bm-printing .bm-section { break-inside: avoid; }
  body.bm-printing .bm-card { break-inside: avoid; page-break-inside: avoid; }
  body.bm-printing .bm-card > div { border-color: #ddd !important; background: #fff !important; }
  body.bm-printing .bm-card-text, body.bm-printing .bm-card-text * { color: #111 !important; }
  body.bm-printing .bm-print-overlay, body.bm-printing .bm-print-overlay * {
    -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
  }
}
`;

/** One row from public.get_brand_mentions. */
interface MentionRow {
  kind: 'post' | 'story' | 'owned';
  mention_id: string;
  brand_id: string | null;
  brand_name: string | null;
  partnership_type: string | null;
  source: string | null;
  detected_at: string | null;
  ig_id: string | null;
  instagram_provider_id: string | null;
  handle: string | null;
  caption: string | null;
  likes: number | null;
  comments: number | null;
  posted_at: string | null;
  thumb_url: string | null;
  permalink: string | null;
  is_paid_partnership: boolean | null;
  post_type?: string | null;
  child_posts?: unknown;
  video_url?: string | null;
}

interface BrandCard {
  total_mentions: number; post_mentions: number; story_mentions: number;
  distinct_creators: number; distinct_advertisers: number;
  paid_share_pct: number | string | null; median_er: number | string | null;
  median_followers: number | null; first_seen: string | null; last_seen: string | null;
  // identity (from brand_instagram) + own content (from brand_ig_posts)
  handle: string | null; full_name: string | null; bio: string | null;
  ig_category_name: string | null; avatar_url: string | null;
  brand_followers: number | null; brand_posts_count: number | null;
  is_verified: boolean | null; external_url: string | null;
  own_posts_total: number | null; own_last_post: string | null; own_posts_30d: number | null;
  own_avg_engagement: number | string | null; own_er: number | string | null;
}
interface BrandTopCreator {
  handle: string | null; provider_id: string | null; mentions: number; story_mentions: number;
  paid_share_pct: number | string | null; followers: number | null; engagement_rate: number | string | null;
}
interface BrandTrendPt { month: string; mentions: number; posts: number; stories: number; }

interface Props {
  brandId: string;
  brandName: string;
  categoryId?: string | null;
  subcategoryId?: string | null;
  windowFrom: string;
  windowTo: string;
  onClose: () => void;
}

// Brand summary — the brand-side counterpart to the creator card. KPI tiles, a
// mention trend, and the top creators using the brand (click one to filter the
// evidence grid below to that creator).
function BrandSummary({ brandId, windowFrom, windowTo, activeHandle, onPickCreator }: {
  brandId: string; windowFrom: string; windowTo: string;
  activeHandle: string | null; onPickCreator: (handle: string) => void;
}) {
  const { t, i18n } = useTranslation('customer');
  const lang = i18n.language || 'en';
  const compact = (n: number | null) => (n == null ? '—' : new Intl.NumberFormat(lang, { notation: 'compact', maximumFractionDigits: 1 }).format(n));
  const fmtDate = (d: string | null) => (d ? new Date(d + 'T00:00:00').toLocaleDateString(lang, { month: 'short', year: '2-digit' }) : '—');
  const num = (v: number | string | null | undefined) => (v == null ? 0 : Number(v) || 0);

  const cardQ = useSupabaseQuery<BrandCard[]>(
    ['brand-card', brandId, windowFrom, windowTo],
    async () => supabase.rpc('get_social_brand_card', { p_brand_id: brandId, p_from: windowFrom, p_to: windowTo }),
  );
  const topQ = useSupabaseQuery<BrandTopCreator[]>(
    ['brand-top-creators', brandId, windowFrom, windowTo],
    async () => supabase.rpc('get_social_brand_top_creators', { p_brand_id: brandId, p_from: windowFrom, p_to: windowTo, p_limit: 40 }),
  );
  const trendQ = useSupabaseQuery<BrandTrendPt[]>(
    ['brand-trend', brandId, windowFrom, windowTo],
    async () => supabase.rpc('get_social_brand_trend', { p_brand_id: brandId, p_from: windowFrom, p_to: windowTo }),
  );

  const card = cardQ.data?.[0];
  const verif = useHandleVerification([card?.handle, ...((topQ.data ?? []).map((c) => c.handle))]);
  if (cardQ.isLoading) return <div className="bm-no-print" style={{ marginBottom: 16 }}><Skeleton height={150} /></div>;
  // Identity (from brand_instagram) + owned-content (from brand_ig_posts) always
  // exist even when NOBODY mentioned the brand — only the earned/mention analytics
  // depend on mentions. Render the card whenever we have a row; gate the rest.
  if (!card) return null;
  const hasMentions = num(card.total_mentions) > 0;

  const tile = (label: string, value: string, hint?: string, color?: string) => (
    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: color ?? '#e6ecf5', marginTop: 2 }}>{value}</div>
      {hint && <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--dim)', marginTop: 1 }}>{hint}</div>}
    </div>
  );

  const trend = trendQ.data ?? [];

  // Owned side: brand's own audience + own-content engagement rate.
  // Earned side: how much wider a typical mentioning creator's audience is.
  const brandFollowers = card.brand_followers ?? 0;
  const ownEr = num(card.own_er);
  const medEr = num(card.median_er);
  const medFollowers = card.median_followers ?? 0;
  const reachMult = brandFollowers > 0 && medFollowers > 0 ? medFollowers / brandFollowers : null;
  const lastOwn = card.own_last_post ? new Date(card.own_last_post + 'T00:00:00') : null;
  const daysSinceOwn = lastOwn ? Math.floor((Date.now() - lastOwn.getTime()) / 86400000) : null;
  const alive = daysSinceOwn != null && daysSinceOwn <= 30;
  const cleanUrl = (u: string | null) => (u ?? '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
  const website = card.external_url && !card.external_url.includes('@') ? card.external_url : null;

  return (
    <div className="bm-no-print" style={{ marginBottom: 18 }}>
      {/* ── Identity strip: who this brand is (from its own IG account) ── */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingBottom: 16, marginBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <Avatar src={card.avatar_url} name={card.handle ?? card.full_name ?? '?'} size={60} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {card.handle && <span style={{ fontSize: 'var(--fs-body)', color: 'var(--accent-text)', fontWeight: 'var(--fw-semibold)' }}>@{card.handle}</span>}
            {card.handle && <VerifiedBadge verified={card.is_verified || verif(card.handle).verified} claimed={verif(card.handle).claimed} size={18} />}
            {card.ig_category_name && (
              <span style={{ fontSize: 'var(--fs-caption)', color: '#c9a9f0', background: 'rgba(155,93,229,0.12)', border: '1px solid rgba(155,93,229,0.3)', borderRadius: 999, padding: '1px 9px' }}>{card.ig_category_name}</span>
            )}
          </div>
          {card.bio && (
            <div style={{ fontSize: 'var(--fs-caption)', color: '#aeb7c2', marginTop: 5, maxWidth: '52ch', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{card.bio}</div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 8, fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>
            {brandFollowers > 0 && <span><b style={{ color: '#e6ecf5', fontSize: 'var(--fs-body)' }}>{compact(brandFollowers)}</b> {t('social.brand.id.followers', { defaultValue: 'подписчиков бренда' })}</span>}
            {card.brand_posts_count != null && <span><b style={{ color: '#e6ecf5', fontSize: 'var(--fs-body)' }}>{compact(card.brand_posts_count)}</b> {t('social.brand.id.posts', { defaultValue: 'постов' })}</span>}
            {daysSinceOwn != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: alive ? '#5fd17a' : '#c99' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: alive ? '#2ecc71' : '#c0392b', display: 'inline-block' }} />
                {alive ? t('social.brand.id.active', { defaultValue: 'активен' }) : t('social.brand.id.dormant', { defaultValue: 'спит' })} · {t('social.brand.id.lastPost', { defaultValue: 'последний пост', })} {lastOwn?.toLocaleDateString(lang, { day: 'numeric', month: 'short' })}
              </span>
            )}
            {website && <a href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{cleanUrl(website)} ↗</a>}
          </div>
        </div>
      </div>

      {/* ── Owned vs Earned reach ── */}
      {(brandFollowers > 0 || medFollowers > 0) && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{t('social.brand.evo.title', { defaultValue: 'Собственный охват против блогерского' })}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderLeft: '3px solid #9B5DE5', borderRadius: 10, padding: '11px 14px' }}>
              <div style={{ fontSize: 'var(--fs-micro)', color: '#c9a9f0', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('social.brand.evo.owned', { defaultValue: 'Свой аккаунт (owned)' })}</div>
              <div style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', marginTop: 3 }}>{compact(brandFollowers)}{ownEr > 0 && <span style={{ fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-regular)', color: 'var(--muted)' }}> · ER {ownEr.toFixed(1)}%</span>}</div>
              <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--muted)', marginTop: 3 }}>{t('social.brand.evo.ownedNote', { defaultValue: 'Аудитория и вовлечённость собственных постов бренда.' })}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderLeft: '3px solid #4a9eff', borderRadius: 10, padding: '11px 14px' }}>
              <div style={{ fontSize: 'var(--fs-micro)', color: '#8ab4ff', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('social.brand.evo.earned', { defaultValue: 'Через блогеров (earned)' })}</div>
              {hasMentions ? (
                <div style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', marginTop: 3 }}>{num(card.distinct_creators)}<span style={{ fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-regular)', color: 'var(--muted)' }}> {t('social.brand.evo.creators', { defaultValue: 'блогера · медиана' })} {compact(medFollowers)}</span></div>
              ) : (
                <div style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', marginTop: 3, color: 'var(--dim)' }}>—</div>
              )}
              <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--muted)', marginTop: 3 }}>{hasMentions ? t('social.brand.evo.earnedNote', { defaultValue: 'Аудитория, до которой бренд дотянулся руками блогеров.' }) : t('social.brand.evo.earnedNone', { defaultValue: 'Пока никто из блогеров не упоминал бренд в этом окне.' })}</div>
            </div>
          </div>
          {reachMult != null && reachMult >= 1.3 && (
            <div style={{ marginTop: 10, background: 'linear-gradient(90deg, rgba(74,158,255,0.08), rgba(155,93,229,0.06))', border: '1px solid rgba(74,158,255,0.2)', borderRadius: 10, padding: '10px 13px', fontSize: 'var(--fs-small)', color: '#cdd6e2', lineHeight: 1.5 }}>
              {t('social.brand.evo.verdict', {
                defaultValue: 'Блогеры расширяют охват бренда в ~{{mult}}×: типичная аудитория упоминающего блогера ({{med}}) шире собственной ({{own}}).',
                mult: reachMult.toFixed(1), med: compact(medFollowers), own: compact(brandFollowers),
              })}
              {ownEr > 0 && medEr > 0 && ` ${t('social.brand.evo.erNote', { defaultValue: 'Свой контент вовлекает на {{own}}% против {{med}}% медианных у блогеров.', own: ownEr.toFixed(1), med: medEr.toFixed(1) })}`}
            </div>
          )}
        </div>
      )}

      {hasMentions && (<>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
        {tile(t('social.brand.kpi.mentions'), num(card.total_mentions).toLocaleString(i18nInst.language),
          t('social.brand.kpi.mentionsHint', { posts: num(card.post_mentions), stories: num(card.story_mentions) }), '#bcd0ff')}
        {tile(t('social.brand.kpi.creators'), num(card.distinct_creators).toLocaleString(i18nInst.language))}
        {tile(t('social.brand.kpi.medianEr'), `${num(card.median_er).toFixed(1)}%`, t('social.brand.kpi.medianErHint', { defaultValue: 'у блогеров' }), '#5fd17a')}
        {tile(t('social.brand.kpi.medianFollowers'), compact(card.median_followers), t('social.brand.kpi.medianFollowersHint', { defaultValue: 'у блогеров, не бренда' }))}
        {tile(t('social.brand.kpi.paidShare'), `${num(card.paid_share_pct).toFixed(0)}%`)}
        {tile(t('social.brand.kpi.period'), `${fmtDate(card.first_seen)} → ${fmtDate(card.last_seen)}`)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr)', gap: 14 }}>
        <div>
          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('social.brand.trend')}</div>
          {trend.length < 2 ? (
            <div style={{ fontSize: 'var(--fs-small)', color: 'var(--dim)' }}>—</div>
          ) : (
            <Suspense fallback={<Skeleton height={170} />}>
              <BrandTrendNivo trend={trend} lang={lang} postsLabel={t('social.brand.trend.posts')} storiesLabel={t('social.brand.trend.stories')} />
            </Suspense>
          )}
        </div>
        <div>
          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6, display: 'flex', gap: 8, alignItems: 'baseline' }}>
            {t('social.brand.topCreators')}
            <span style={{ fontSize: 'var(--fs-micro)', textTransform: 'none', letterSpacing: 0, color: 'var(--dim)' }}>· {t('social.brand.creatorHint')}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {(topQ.data ?? []).slice(0, 8).map((c) => (
              <button
                key={c.provider_id ?? c.handle ?? ''} type="button"
                onClick={() => c.handle && onPickCreator(c.handle)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', textAlign: 'left',
                  background: activeHandle === c.handle ? 'rgba(74,158,255,0.14)' : 'transparent',
                  border: '1px solid ' + (activeHandle === c.handle ? 'rgba(74,158,255,0.4)' : 'transparent'),
                  borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text-2)', fontSize: 'var(--fs-body)',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, overflow: 'hidden' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{c.handle}</span>
                  <VerifiedBadge {...verif(c.handle)} size={14} />
                </span>
                <span style={{ display: 'inline-flex', gap: 10, flexShrink: 0, color: 'var(--muted)', fontSize: 'var(--fs-caption)' }}>
                  <span title="followers">{compact(c.followers)}</span>
                  <span title="ER" style={{ color: '#5fd17a' }}>{num(c.engagement_rate).toFixed(1)}%</span>
                  <span title="mentions" style={{ color: 'var(--accent-text)', minWidth: 18, textAlign: 'right' }}>{c.mentions}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <BrandCreatorScatter creators={topQ.data ?? []} activeHandle={activeHandle} onPick={onPickCreator} lang={lang} />
      </>)}
    </div>
  );
}

function median(a: number[]) {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y); const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}


// Efficiency scatter for a brand's own creators: followers (log x) vs ER (y),
// colour = ER above/below the brand's roster median (efficient vs paying-too-much),
// size = how heavily the brand uses them. Answers "which creators does this brand
// overpay?" — decisive because the population is small and every point is named.
function BrandCreatorScatter({ creators, activeHandle, onPick, lang }: {
  creators: BrandTopCreator[]; activeHandle: string | null; onPick: (h: string) => void; lang: string;
}) {
  const { t } = useTranslation('customer');
  const [hover, setHover] = useState<string | null>(null);
  const compact = (n: number) => new Intl.NumberFormat(lang, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  // Only creators with real follower AND ER data — a null/0 ER is MISSING data,
  // not "0% efficient", so it must not be plotted as an inefficiency.
  const pts = creators
    .filter((c) => c.handle && (c.followers ?? 0) > 0 && Number(c.engagement_rate) > 0)
    .map((c) => ({ handle: c.handle as string, followers: c.followers as number, er: Number(c.engagement_rate), mentions: Number(c.mentions) || 0 }));
  if (pts.length < 4) return null;

  const W = 680, H = 280, padL = 40, padR = 16, padT = 22, padB = 30;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const fols = pts.map((p) => p.followers);
  const logMin = Math.log10(Math.max(Math.min(...fols) * 0.7, 500));
  const logMax = Math.log10(Math.max(Math.max(...fols) * 1.4, 5000));
  const ersSorted = [...pts.map((p) => p.er)].sort((a, b) => a - b);
  const p90 = ersSorted[Math.floor(ersSorted.length * 0.9)] ?? 10;
  const yMax = Math.min(35, Math.max(6, p90 * 1.2)); // robust cap — no outlier collapse
  const medF = median(fols);
  const medEr = median(pts.map((p) => p.er));
  const maxM = Math.max(...pts.map((p) => p.mentions), 1);
  const xAt = (f: number) => padL + ((Math.log10(Math.max(f, 1)) - logMin) / (logMax - logMin)) * plotW;
  // sqrt y-scale spreads the low-ER mass (ER is right-skewed: many low, few high)
  const yAt = (e: number) => padT + plotH - Math.sqrt(Math.min(e, yMax) / yMax) * plotH;
  const rOf = (m: number) => 4 + (m / maxM) * 12;
  const colorOf = (er: number) => (er >= medEr ? '#5fd17a' : '#e0896a');

  // Greedy non-overlapping labels, biggest-usage first (hovered/active always shown).
  const overlaps = (a: number[], b: number[]) => !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
  const boxes: number[][] = [];
  const labels: { h: string; x: number; y: number; anchor: 'start' | 'end' }[] = [];
  for (const p of [...pts].sort((a, b) => b.mentions - a.mentions)) {
    if (labels.length >= 14) break;
    const x = xAt(p.followers), y = yAt(p.er), r = rOf(p.mentions);
    const w = p.handle.length * 5.4 + 10;
    let lx = x + r + 3; let anchor: 'start' | 'end' = 'start';
    let box = [lx, y - 6, lx + w, y + 6];
    if (box[2] > W - 2) { lx = x - r - 3; anchor = 'end'; box = [lx - w, y - 6, lx, y + 6]; }
    if (box[0] < padL) continue;
    if (boxes.some((b) => overlaps(box, b))) continue;
    boxes.push(box); labels.push({ h: p.handle, x: lx, y: y + 3, anchor });
  }

  const yTicks = [1, 2, 3, 5, 7, 10, 15, 20, 25, 30].filter((v) => v <= yMax);
  const xTicks = [1000, 10000, 100000, 1000000].filter((v) => xAt(v) > padL + 8 && xAt(v) < W - padR - 8);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>{t('social.brand.scatter.title')}</div>
      <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--dim)', marginBottom: 8 }}>{t('social.brand.scatter.hint')}</div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} role="img" aria-label="Brand creators: followers vs engagement">
        {/* costly-reach zone: big followers, below-median ER = where the brand likely overpays */}
        <rect x={xAt(medF)} y={yAt(medEr)} width={W - padR - xAt(medF)} height={padT + plotH - yAt(medEr)} fill="rgba(224,137,106,0.06)" />
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={padL} y1={yAt(v)} x2={W - padR} y2={yAt(v)} stroke="rgba(255,255,255,0.045)" />
            <text x={padL - 5} y={yAt(v) + 3} textAnchor="end" fontSize={9} fill="#667">{v}%</text>
          </g>
        ))}
        {xTicks.map((v) => (
          <text key={v} x={xAt(v)} y={H - padB + 14} textAnchor="middle" fontSize={9} fill="#667">{compact(v)}</text>
        ))}
        <line x1={xAt(medF)} y1={padT} x2={xAt(medF)} y2={padT + plotH} stroke="rgba(255,255,255,0.14)" strokeDasharray="3 3" />
        <line x1={padL} y1={yAt(medEr)} x2={W - padR} y2={yAt(medEr)} stroke="rgba(255,255,255,0.14)" strokeDasharray="3 3" />
        <text x={W - padR - 3} y={padT + plotH - 5} textAnchor="end" fontSize={10} fill="rgba(224,137,106,0.85)">{t('social.brand.scatter.costlyReach')}</text>
        <text x={padL + 3} y={padT + 11} fontSize={10} fill="rgba(95,209,122,0.85)">{t('social.brand.scatter.efficient')}</text>
        {pts.map((p) => {
          const x = xAt(p.followers), y = yAt(p.er), r = rOf(p.mentions);
          const active = activeHandle === p.handle || hover === p.handle;
          return (
            <g key={p.handle} style={{ cursor: 'pointer' }} onClick={() => onPick(p.handle)} onMouseEnter={() => setHover(p.handle)} onMouseLeave={() => setHover(null)}>
              <circle cx={x} cy={y} r={r} fill={colorOf(p.er)} fillOpacity={active ? 1 : 0.72} stroke={active ? '#fff' : 'rgba(0,0,0,0.35)'} strokeWidth={active ? 2 : 1} />
              {p.er > yMax && <text x={x} y={y - r - 2} textAnchor="middle" fontSize={9} fill="#e0896a">▲</text>}
            </g>
          );
        })}
        {labels.map((l) => {
          const active = activeHandle === l.h || hover === l.h;
          return <text key={l.h} x={l.x} y={l.y} textAnchor={l.anchor} fontSize={9} fill={active ? '#fff' : '#9aabbc'}>@{l.h}</text>;
        })}
        {hover && !labels.some((l) => l.h === hover) && (() => {
          const p = pts.find((q) => q.handle === hover); if (!p) return null;
          const x = xAt(p.followers), y = yAt(p.er), r = rOf(p.mentions);
          const right = x + r + 60 < W;
          return <text x={right ? x + r + 3 : x - r - 3} y={y + 3} textAnchor={right ? 'start' : 'end'} fontSize={9} fill="#fff">@{p.handle}</text>;
        })()}
      </svg>
      <div style={{ display: 'flex', gap: 14, fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 4, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#5fd17a', display: 'inline-block' }} />{t('social.brand.scatter.efficient')}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#e0896a', display: 'inline-block' }} />{t('social.brand.scatter.lessEfficient')}</span>
        <span style={{ color: 'var(--dim)' }}>{t('social.brand.scatter.sizeNote')}</span>
      </div>
    </div>
  );
}

export default function BrandMentionsModal({
  brandId,
  brandName,
  categoryId,
  subcategoryId,
  windowFrom,
  windowTo,
  onClose,
}: Props) {
  const { t } = useTranslation('customer');
  const [viewing, setViewing] = useState<MentionRow | null>(null);
  const [selHandle, setSelHandle] = useState<string | null>(null);
  const [selGroup, setSelGroup] = useState<CollabGroup | null>(null);
  const [sortMode, setSortMode] = useState<'recent' | 'engaged'>('recent');
  const [postView, setPostView] = useState<'earned' | 'owned'>('earned');
  const overlayRef = useRef<HTMLDivElement>(null);

  useCloseOnTabSwitch(onClose);
  useBodyScrollLock();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q = useSupabaseQuery<MentionRow[]>(
    ['brand-mentions', brandId, windowFrom, windowTo, categoryId ?? '', subcategoryId ?? ''],
    async () =>
      supabase.rpc('get_brand_mentions', {
        p_brand_id: brandId,
        p_window_from: windowFrom,
        p_window_to: windowTo,
        p_category_id: categoryId ?? null,
        p_subcategory_id: subcategoryId ?? null,
        // Load the full mention set (busiest brand ≈ 170) so clicking any creator in
        // the scatter / top-creators list — both computed over ALL mentions — actually
        // filters to their posts, instead of matching nothing outside a 60-row window.
        p_limit: 500,
      }),
  );

  // Brand's OWN posts (from brand_ig_posts) — the "Свои" tab. Lazy: only fetched
  // once the tab is first opened, then cached.
  // Own content is the brand's own account history — reference material, not
  // window-bound analytics. Show it all-time (window omitted → NULL = no date
  // filter), matching the all-time OWNED summary so the tab is never empty just
  // because the brand didn't post inside the selected mention year.
  const ownQ = useSupabaseQuery<MentionRow[]>(
    ['brand-own-posts', brandId],
    async () => supabase.rpc('get_brand_own_posts', { p_brand_id: brandId, p_limit: 90 }),
  );
  const ownRows = [...ownQ.data ?? []].sort((a, b) => sortMode === 'engaged'
    ? engagementOf(b) - engagementOf(a) : (b.posted_at ?? '').localeCompare(a.posted_at ?? ''));

  const rows = q.data ?? [];
  const postCount = rows.filter((r) => r.kind === 'post').length;
  const storyCount = rows.filter((r) => r.kind === 'story').length;

  // Print just this modal. Append a CLONE of the overlay to <body> (a direct body
  // child lays out in normal flow → paginates; the modal is otherwise nested deep
  // in #root where absolute/fixed positioning won't break across pages). We clone
  // rather than MOVE the React-owned node so React's unmount/reconciliation can
  // never break (a broken unmount would skip the scroll-lock cleanup → stuck page).
  // The clone is a static snapshot — images already carry resolved signed-URL src.
  const handlePrint = () => {
    const el = overlayRef.current;
    if (!el) { window.print(); return; }
    const clone = el.cloneNode(true) as HTMLElement;
    document.body.appendChild(clone);
    document.body.classList.add('bm-printing');
    const cleanup = () => {
      if (!document.body.classList.contains('bm-printing')) return;
      document.body.classList.remove('bm-printing');
      clone.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    // Fallback if afterprint never fires (some browsers).
    setTimeout(cleanup, 1500);
  };

  // Collaborators present (for the filter) + the active filtered set.
  const counts = new Map<string, number>();
  for (const r of rows) if (r.handle) counts.set(r.handle, (counts.get(r.handle) ?? 0) + 1);
  const collaborators = [...counts.entries()].map(([handle, n]) => ({ handle, n })).sort((a, b) => b.n - a.n).slice(0, 20);

  // Collaboration-type counts (reliable substitute for a paid/organic split, which
  // has no data — is_paid is 0 across the board). Counted after the creator filter.
  const afterHandle = selHandle ? rows.filter((r) => r.handle === selHandle) : rows;
  const groupCounts = new Map<CollabGroup, number>();
  for (const r of afterHandle) { const g = srcGroup(r.source); groupCounts.set(g, (groupCounts.get(g) ?? 0) + 1); }
  const groupsPresent = GROUP_ORDER.filter((g) => groupCounts.has(g));

  const filtered = [...(selGroup ? afterHandle.filter((r) => srcGroup(r.source) === selGroup) : afterHandle)]
    .sort((a, b) => sortMode === 'engaged'
      ? engagementOf(b) - engagementOf(a)
      : (b.posted_at ?? '').localeCompare(a.posted_at ?? ''));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('social.analytics.mentions.title', { defaultValue: 'Mentions: {{name}}', name: brandName })}
      onClick={onClose}
      ref={overlayRef}
      className="bm-print-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '4vh 16px', overflowY: 'auto', backdropFilter: 'blur(6px)',
      }}
    >
      <style>{PRINT_CSS}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bm-print-root"
        style={{
          width: '100%', maxWidth: 1100, background: 'var(--bg-elev, #161b22)',
          border: '1px solid var(--border-strong, #2a2d35)', borderRadius: 14,
          padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        <SectionErrorBoundary name="brand-mentions">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-caption)', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--muted)' }}>
                {t('social.analytics.mentions.kicker', { defaultValue: 'Brand mentions' })}
              </div>
              <h2 style={{ margin: '4px 0 0', fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)' }}>{brandName}</h2>
              <div style={{ marginTop: 6, fontSize: 'var(--fs-body)', color: 'var(--muted)' }}>
                {q.isLoading
                  ? t('common.loading', { defaultValue: 'Loading…' })
                  : t('social.analytics.mentions.count', {
                      defaultValue: '{{posts}} posts · {{stories}} stories · window {{from}} → {{to}}',
                      posts: postCount, stories: storyCount, from: windowFrom, to: windowTo,
                    })}
              </div>
            </div>
            <div className="bm-no-print" style={{ display: 'flex', gap: 8 }}>
              <button
                type="button" onClick={handlePrint}
                aria-label={t('social.analytics.mentions.print', { defaultValue: 'Print' })}
                title={t('social.analytics.mentions.print', { defaultValue: 'Print' })}
                style={{
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'var(--text)',
                  height: 32, padding: '0 12px', borderRadius: 8, cursor: 'pointer', fontSize: 'var(--fs-body)',
                  lineHeight: 1, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              ><NamedIcon name="news" size={14} /> {t('social.analytics.mentions.print', { defaultValue: 'Print' })}</button>
              <button
                type="button" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}
                style={{
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'var(--text)',
                  width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 'var(--fs-label)', lineHeight: 1, fontFamily: 'inherit',
                }}
              >×</button>
            </div>
          </div>

          <BrandSummary
            brandId={brandId}
            windowFrom={windowFrom}
            windowTo={windowTo}
            activeHandle={selHandle}
            onPickCreator={(h) => setSelHandle((prev) => (prev === h ? null : h))}
          />

          {q.isLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={220} />)}
            </div>
          ) : q.error ? (
            <div style={{ color: 'var(--danger)' }}>{String((q.error as Error)?.message ?? q.error)}</div>
          ) : (rows.length === 0 && ownRows.length === 0) ? (
            <div style={{
              padding: 24, color: 'var(--muted)', textAlign: 'center', background: 'rgba(15,45,100,0.05)',
              border: '1px dashed rgba(15,45,100,0.3)', borderRadius: 10,
            }}>
              {t('social.analytics.mentions.empty', { defaultValue: 'No mentions found in this window.' })}
            </div>
          ) : (
            <>
              {/* Primary view switch: earned mentions ↔ the brand's own posts */}
              <div className="bm-no-print" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                <FilterChip active={postView === 'earned'} onClick={() => setPostView('earned')}>
                  {t('social.analytics.mentions.tabEarned', { defaultValue: 'Упоминания' })} · {rows.length}
                </FilterChip>
                <FilterChip active={postView === 'owned'} owned onClick={() => setPostView('owned')}>
                  {t('social.analytics.mentions.tabOwned', { defaultValue: 'Свои' })} · {ownQ.isLoading ? '…' : ownRows.length}
                </FilterChip>
              </div>
              {postView === 'owned' ? (
                ownRows.length === 0 ? (
                  <div style={{ padding: 24, color: 'var(--muted)', textAlign: 'center' }}>
                    {t('social.analytics.mentions.ownEmpty', { defaultValue: 'Собственных постов бренда в этом окне нет.' })}
                  </div>
                ) : (
                  <div className="bm-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, alignItems: 'start' }}>
                    {ownRows.map((r) => <MentionCard key={r.mention_id} r={r} onClick={() => setViewing(r)} />)}
                  </div>
                )
              ) : rows.length === 0 ? (
                <div style={{ padding: 24, color: 'var(--muted)', textAlign: 'center' }}>
                  {t('social.analytics.mentions.empty', { defaultValue: 'No mentions found in this window.' })}
                </div>
              ) : (
              <>
              {/* Collaborator filter */}
              {collaborators.length > 1 && (
                <div className="bm-no-print" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                  <FilterChip active={!selHandle} onClick={() => setSelHandle(null)}>
                    {t('social.analytics.mentions.all', { defaultValue: 'All' })} · {rows.length}
                  </FilterChip>
                  {collaborators.map((c) => (
                    <FilterChip key={c.handle} active={selHandle === c.handle} onClick={() => setSelHandle(c.handle)}>
                      @{c.handle} · {c.n}
                    </FilterChip>
                  ))}
                </div>
              )}
              {/* Collaboration-type focus + sort */}
              <div className="bm-no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {groupsPresent.length > 1 && (
                    <>
                      <FilterChip active={!selGroup} onClick={() => setSelGroup(null)}>
                        {t('social.analytics.mentions.allTypes', { defaultValue: 'All types' })}
                      </FilterChip>
                      {groupsPresent.map((g) => (
                        <FilterChip key={g} active={selGroup === g} onClick={() => setSelGroup(selGroup === g ? null : g)}>
                          {GROUP_LABEL[g]} · {groupCounts.get(g)}
                        </FilterChip>
                      ))}
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                  {(['recent', 'engaged'] as const).map((s) => (
                    <button
                      key={s} type="button" onClick={() => setSortMode(s)}
                      style={{
                        padding: '4px 12px', fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)', cursor: 'pointer', fontFamily: 'inherit',
                        border: 'none', background: sortMode === s ? 'rgba(74,158,255,0.22)' : 'transparent', color: sortMode === s ? 'var(--accent-text)' : 'var(--muted)',
                      }}
                    >
                      {t(`social.analytics.mentions.sort.${s}`)}
                    </button>
                  ))}
                </div>
              </div>
              {/* Grouped by collaboration type */}
              {GROUP_ORDER.map((g) => {
                const items = filtered.filter((r) => srcGroup(r.source) === g);
                if (!items.length) return null;
                return (
                  <section key={g} className="bm-section" style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-small)', color: 'var(--muted)', marginBottom: 8 }}>
                      {GROUP_ICON[g]} {GROUP_LABEL[g]} <span style={{ color: 'var(--dim)' }}>· {items.length}</span>
                    </div>
                    <div className="bm-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, alignItems: 'start' }}>
                      {items.map((r) => <MentionCard key={r.mention_id} r={r} onClick={() => setViewing(r)} />)}
                    </div>
                  </section>
                );
              })}
              </>
              )}
            </>
          )}
        </SectionErrorBoundary>
      </div>

      {viewing && (
        <PostViewerModal
          post={{
            id: viewing.mention_id,
            post_type: viewing.post_type ?? viewing.kind,
            thumb_url: viewing.thumb_url,
            video_url: viewing.video_url ?? null,
            child_posts: viewing.child_posts,
            caption: viewing.caption,
            likes: viewing.likes,
            comments: viewing.comments,
            posted_at: viewing.posted_at,
            permalink: viewing.permalink,
            handle: viewing.handle,
            brand_name: viewing.brand_name,
          }}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function MentionCard({ r, onClick }: { r: MentionRow; onClick: () => void }) {
  const { t } = useTranslation('customer');
  const captionShort = r.caption && r.caption.length > 140 ? `${r.caption.slice(0, 140)}…` : r.caption ?? '';
  const dateLabel = r.posted_at ? new Date(r.posted_at).toLocaleDateString(currentLocale()) : '—';

  return (
    <button
      type="button" onClick={onClick}
      className="bm-card"
      style={{ background: 'transparent', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', color: 'inherit', width: '100%' }}
      title={t('social.analytics.mentions.openPost', { defaultValue: 'View post' })}
    >
      <div style={{
        background: 'rgba(15,45,100,0.10)', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 10,
        overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%',
      }}>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', flexShrink: 0, overflow: 'hidden', background: 'rgba(0,0,0,0.4)' }}>
          {r.thumb_url ? (
            <SignedImg src={r.thumb_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dim)', fontSize: 'var(--fs-h1)' }}>◯</div>
          )}
          <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {r.kind === 'owned' ? (
              <span style={chipStyle('#9B5DE5')}>{t('social.analytics.mentions.chip.own', { defaultValue: 'СВОЙ' })}</span>
            ) : (
              <span style={chipStyle(r.kind === 'post' ? '#4a9eff' : '#9B5DE5')}>
                {r.kind === 'post'
                  ? t('social.analytics.mentions.chip.post', { defaultValue: 'POST' })
                  : t('social.analytics.mentions.chip.story', { defaultValue: 'STORY' })}
              </span>
            )}
            {r.is_paid_partnership && (
              <span style={chipStyle('#f5b700')}>{t('social.analytics.mentions.chip.paid', { defaultValue: 'PAID' })}</span>
            )}
          </div>
        </div>
        <div className="bm-card-text" style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--accent-text)' }}>{r.handle ? `@${r.handle}` : '—'}</span>
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)' }}>{dateLabel}</span>
          </div>
          {/* Always rendered with a reserved 2-line height so every card is the same
              height → no ragged grid gaps under shorter cards. */}
          <div style={{
            fontSize: 'var(--fs-small)', color: 'var(--text-2)', lineHeight: 1.4, minHeight: '2.8em', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{captionShort}</div>
          <div style={{ marginTop: 'auto', display: 'flex', gap: 10, fontSize: 'var(--fs-caption)', color: 'var(--muted)', paddingTop: 4, minHeight: 17 }}>
            {r.kind !== 'story' && (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><NamedIcon name="heart" size={11} color="#9aa" /> {(r.likes ?? 0).toLocaleString(i18nInst.language)}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><NamedIcon name="chat" size={11} color="#9aa" /> {(r.comments ?? 0).toLocaleString(i18nInst.language)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function FilterChip({ active, onClick, children, owned }: { active: boolean; onClick: () => void; children: React.ReactNode; owned?: boolean }) {
  const on = owned ? '#9B5DE5' : '#2563eb';
  return (
    <button
      type="button" onClick={onClick}
      style={{
        fontSize: 'var(--fs-small)', padding: '5px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
        background: active ? on : '#14171c',
        color: active ? '#fff' : (owned ? '#c9a9f0' : 'var(--muted)'),
        border: `1px solid ${active ? on : (owned ? 'rgba(155,93,229,0.45)' : '#2a2d35')}`,
      }}
    >{children}</button>
  );
}

function chipStyle(color: string): React.CSSProperties {
  return {
    fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4,
    background: 'rgba(0,0,0,0.55)', color, letterSpacing: 0.5,
    border: `1px solid ${color}`, backdropFilter: 'blur(4px)',
  };
}
